/**
 * Spades bot.
 *
 * - Bidding: expected-trick estimator (spade length/honours, side-suit aces and kings, ruffing potential),
 *   shaded by difficulty; bids Nil only on very weak hands at NORMAL and above.
 * - Play: partnership-aware heuristic — protects a partner's Nil, ducks when its own Nil is at stake, lets a
 *   winning partner take the trick, wins as cheaply as possible when the team still needs tricks, and avoids
 *   piling up bags once the contract is made. HARD/EXPERT/NORMAL track played cards to spot master cards.
 * The bot only ever reads its own seat's view.
 */
import { buildDeck, parseCard, SUITS, type CardId, type Suit } from '../../core/cards.js';
import { chance, type BotContext, type BotEngine } from '../../bots/types.js';
import { spadesPartnerOf, spadesTeamOf, spadesTrickWinner, type SpadesAction, type SpadesLegal, type SpadesTrickPlay, type SpadesView } from './engine.js';

const TRUMP: Suit = 'S';
const ESTIMATE_SCALE = 1.5;

const power = (id: CardId) => {
  const c = parseCard(id);
  return (c.suit === TRUMP ? 100 : 0) + c.rank;
};
const lowest = (cards: CardId[]) => [...cards].sort((a, b) => power(a) - power(b))[0]!;
const highest = (cards: CardId[]) => [...cards].sort((a, b) => power(b) - power(a))[0]!;

export function estimateSpadesTricks(hand: readonly CardId[]): number {
  const cards = hand.map(parseCard);
  const bySuit = new Map<Suit, number[]>();
  for (const s of SUITS) bySuit.set(s, []);
  for (const c of cards) bySuit.get(c.suit)!.push(c.rank);
  for (const ranks of bySuit.values()) ranks.sort((a, b) => b - a);
  const spades = bySuit.get(TRUMP)!;
  const sLen = spades.length;
  let est = 0;
  for (const [suit, ranks] of bySuit) {
    if (suit === TRUMP) continue;
    const len = ranks.length;
    if (ranks.includes(14)) est += len >= 7 ? 0.7 : 1;
    if (ranks.includes(13)) est += len >= 2 && len <= 4 ? (ranks.includes(14) ? 0.8 : 0.5) : 0.1;
  }
  for (const r of spades) {
    if (r === 14) est += 1;
    else if (r === 13) est += sLen >= 2 ? 0.9 : 0.5;
    else if (r === 12) est += sLen >= 3 ? 0.7 : 0.3;
    else if (r === 11) est += sLen >= 4 ? 0.45 : 0.1;
  }
  if (sLen > 4) est += (sLen - 4) * 0.8;
  const spareSpades = Math.max(0, sLen - spades.filter((r) => r >= 12).length);
  let ruffs = 0;
  for (const [suit, ranks] of bySuit) {
    if (suit === TRUMP) continue;
    if (ranks.length === 0) ruffs += 1;
    else if (ranks.length === 1) ruffs += 0.5;
  }
  est += Math.min(ruffs, spareSpades) * 0.6;
  // Calibrated so the mean over random deals is ~13/4 tricks; the raw honour counts alone under-bid badly.
  return est * ESTIMATE_SCALE;
}

/** How safe Nil looks: 0 (hopeless) .. 1 (very safe). */
export function nilSafety(hand: readonly CardId[]): number {
  const cards = hand.map(parseCard);
  const spades = cards.filter((c) => c.suit === TRUMP);
  if (spades.some((c) => c.rank >= 11)) return 0;
  if (cards.some((c) => c.rank === 14)) return 0;
  let risk = 0;
  for (const c of spades) risk += c.rank >= 9 ? 0.5 : c.rank >= 6 ? 0.2 : 0.05;
  for (const suit of ['H', 'D', 'C'] as const) {
    const ranks = cards.filter((c) => c.suit === suit).map((c) => c.rank).sort((a, b) => b - a);
    const high = ranks.filter((r) => r >= 12).length;
    // A high card without enough low cards beneath it will be forced to win.
    risk += ranks.length === 0 ? 0 : high * (ranks.length <= 2 ? 0.7 : 0.35);
    if (ranks.length >= 6) risk += 0.2;
  }
  return Math.max(0, 1 - risk / 2.4);
}

interface Knowledge {
  unseen: Set<CardId>;
}

function buildKnowledge(view: SpadesView, ctx: BotContext): Knowledge {
  const seen = new Set<CardId>(view.hand);
  view.trick.forEach((p) => seen.add(p.card));
  const remembered = ctx.profile.memory;
  view.tricks.forEach((t, i) => {
    // Forgetful bots only recall the most recent tricks.
    if (remembered >= 1 || i >= view.tricks.length - Math.max(1, Math.round(view.tricks.length * remembered))) {
      for (const p of t.plays) seen.add(p.card);
    }
  });
  return { unseen: new Set(buildDeck().filter((c) => !seen.has(c))) };
}

function isMaster(card: CardId, k: Knowledge): boolean {
  const c = parseCard(card);
  for (const u of k.unseen) {
    const x = parseCard(u);
    if (c.suit === TRUMP ? x.suit === TRUMP && x.rank > c.rank : x.suit === c.suit && x.rank > c.rank) return false;
  }
  return true;
}

function currentWinner(trick: readonly SpadesTrickPlay[]): SpadesTrickPlay | null {
  if (trick.length === 0) return null;
  const seat = spadesTrickWinner(trick);
  return trick.find((p) => p.seat === seat) ?? null;
}

function wouldWin(trick: readonly SpadesTrickPlay[], seat: number, card: CardId): boolean {
  if (trick.length === 0) return true;
  return spadesTrickWinner([...trick, { seat, card }]) === seat;
}

/** Plays the highest card that still loses the trick, else the lowest card (Nil bidders). */
function duck(legal: CardId[], trick: readonly SpadesTrickPlay[], seat: number): CardId {
  if (trick.length === 0) return lowest(legal);
  const losers = legal.filter((c) => !wouldWin(trick, seat, c));
  if (losers.length > 0) return highest(losers);
  return lowest(legal);
}

function dump(legal: CardId[]): CardId {
  const side = legal.filter((c) => parseCard(c).suit !== TRUMP);
  if (side.length === 0) return lowest(legal);
  // Shed from the suit we hold the most of, lowest first — keeps short suits for ruffing.
  const counts = new Map<Suit, number>();
  side.forEach((c) => counts.set(parseCard(c).suit, (counts.get(parseCard(c).suit) ?? 0) + 1));
  const longest = [...counts.entries()].sort((a, b) => b[1] - a[1])[0]![0];
  return lowest(side.filter((c) => parseCard(c).suit === longest));
}

function chooseCard(view: SpadesView, legal: CardId[], ctx: BotContext): CardId {
  if (legal.length === 1) return legal[0]!;
  const me = view.seat!;
  const partner = spadesPartnerOf(me);
  const team = spadesTeamOf(me);
  const trick = view.trick;
  const k = ctx.profile.memory > 0 ? buildKnowledge(view, ctx) : null;

  const iAmNil = view.bids[me] === 0;
  const partnerNil = view.bids[partner] === 0;
  const partnerNilAlive = partnerNil && view.tricksWon[partner] === 0;
  const myNilAlive = iAmNil && view.tricksWon[me] === 0;

  if (myNilAlive) return duck(legal, trick, me);

  const winner = currentWinner(trick);
  if (partnerNilAlive && winner) {
    // Rescue: take the trick if partner is about to win it.
    if (winner.seat === partner) {
      const over = legal.filter((c) => wouldWin(trick, me, c));
      if (over.length > 0) return lowest(over);
    }
  }

  let contract = 0;
  for (const s of [team, team + 2]) if ((view.bids[s] ?? 0) > 0) contract += view.bids[s]!;
  const teamTricks = view.tricksWon[team]! + view.tricksWon[team + 2]!;
  // A live Nil of our own that already failed counts as a normal trick-taker; a live partner Nil needs no tricks from them.
  const need = contract - teamTricks;
  const wantMore = need > 0;
  const last = trick.length === 3;

  if (trick.length === 0) {
    // Leading.
    const nonSpades = legal.filter((c) => parseCard(c).suit !== TRUMP);
    if (partnerNilAlive) {
      // Lead high cards that partner cannot be forced to win with, i.e. just lead our best master.
      const masters = k ? nonSpades.filter((c) => isMaster(c, k)) : nonSpades.filter((c) => parseCard(c).rank === 14);
      if (masters.length) return highest(masters);
    }
    if (wantMore) {
      const masters = k ? nonSpades.filter((c) => isMaster(c, k)) : nonSpades.filter((c) => parseCard(c).rank === 14);
      if (masters.length) return highest(masters);
      const spades = legal.filter((c) => parseCard(c).suit === TRUMP);
      if (k && view.spadesBroken && spades.length >= 3) {
        const top = spades.filter((c) => isMaster(c, k));
        if (top.length) return highest(top);
      }
    }
    if (nonSpades.length) {
      // Lead low from the shortest suit to create voids for ruffing.
      const counts = new Map<Suit, number>();
      nonSpades.forEach((c) => counts.set(parseCard(c).suit, (counts.get(parseCard(c).suit) ?? 0) + 1));
      const shortSuit = [...counts.entries()].sort((a, b) => a[1] - b[1])[0]![0];
      return lowest(nonSpades.filter((c) => parseCard(c).suit === shortSuit));
    }
    return lowest(legal);
  }

  // Following.
  if (winner?.seat === partner && !partnerNilAlive) {
    const pm = k ? isMaster(winner.card, k) : false;
    // Partner is winning: only help if the lead is shaky and someone behind could still beat it.
    if (last || pm || !wantMore) return dump(legal);
    const over = legal.filter((c) => wouldWin(trick, me, c) && parseCard(c).suit === parseCard(winner.card).suit);
    if (over.length && parseCard(winner.card).rank < 10) return lowest(over);
    return dump(legal);
  }
  const winners = legal.filter((c) => wouldWin(trick, me, c));
  if (winners.length === 0) return dump(legal);
  if (!wantMore && !partnerNilAlive) {
    // Contract already made: avoid collecting bags when it is cheap to do so.
    return dump(legal);
  }
  if (last) return lowest(winners);
  const safe = k ? winners.filter((c) => isMaster(c, k) || parseCard(c).suit === TRUMP) : winners;
  if (safe.length) return lowest(safe);
  return lowest(winners);
}

export const spadesBot: BotEngine<SpadesView, SpadesLegal, SpadesAction> = {
  decideAction(view, legal, ctx) {
    if (legal.kind === 'bid') return this.chooseBet!(view, legal, ctx);
    if (legal.kind === 'play') return this.chooseCard!(view, legal, ctx);
    throw new Error('Bot asked to act without legal actions');
  },

  chooseBet(view, legal, ctx) {
    if (legal.kind !== 'bid') return this.handleTimeout(view, legal, ctx);
    const me = view.seat!;
    let est = estimateSpadesTricks(view.hand);
    if (ctx.difficulty === 'BEGINNER') est += (ctx.rng.next() - 0.5) * 2.5;
    else if (ctx.difficulty === 'EASY') est += (ctx.rng.next() - 0.5) * 1.5;
    // Nil is worth a lot but only on a clearly safe hand, and never when partner already went Nil.
    if (ctx.difficulty !== 'BEGINNER' && ctx.difficulty !== 'EASY' && view.bids[spadesPartnerOf(me)] !== 0) {
      const strongPartnerBid = (view.bids[spadesPartnerOf(me)] ?? 0) >= 4;
      if (est < 1.6 && nilSafety(view.hand) >= (strongPartnerBid ? 0.55 : 0.7)) return { type: 'bid', value: 0 };
    }
    const shade = ctx.difficulty === 'HARD' || ctx.difficulty === 'EXPERT' ? 0.2 : 0.05;
    const value = Math.min(legal.max, Math.max(1, Math.round(est - shade)));
    return { type: 'bid', value };
  },

  chooseCard(view, legal, ctx) {
    if (legal.kind !== 'play') return this.handleTimeout(view, legal, ctx);
    const cards = legal.cards;
    if (cards.length === 1) return { type: 'play', card: cards[0]! };
    if (chance(ctx, ctx.profile.mistakeRate)) return { type: 'play', card: cards[ctx.rng.int(cards.length)]! };
    const card = chooseCard(view, cards, ctx);
    return { type: 'play', card: cards.includes(card) ? card : cards[0]! };
  },

  evaluateState(view) {
    const me = view.seat;
    if (me === null) return 0;
    const team = spadesTeamOf(me);
    const diff = view.teamScores[team]! - view.teamScores[1 - team]!;
    return Math.max(0, Math.min(1, 0.5 + diff / 600));
  },

  calculateRisk(view, legal) {
    if (legal.kind === 'bid') return Math.min(1, Math.max(0, (13 - estimateSpadesTricks(view.hand)) / 13));
    return 0;
  },

  handleTimeout(view, legal) {
    if (legal.kind === 'bid') return { type: 'bid', value: Math.max(1, Math.min(legal.max, Math.round(estimateSpadesTricks(view.hand)))) };
    if (legal.kind === 'play') return { type: 'play', card: lowest(legal.cards) };
    throw new Error('No legal action');
  },
};
